import type { Chat } from '@liniya/api-client';
import {
  Archive,
  ArchiveRestore,
  Ban,
  Bell,
  BellOff,
  Eraser,
  LogOut,
  Pin,
  PinOff,
  Radio,
  Trash2,
  X,
} from 'lucide-react';
import { useState } from 'react';

import { Avatar } from '@/components/Avatar';
import { ConfirmDialog } from '@/components/Modal';
import { useBlockPeer, useClearHistory, useLeaveChat, useUpdateMembership } from '@liniya/client-core';
import { usePresenceStore } from '@liniya/client-core';
import { useUiStore } from '@liniya/client-core';

const MUTE_HOURS = 8;

interface Props {
  chat: Chat;
  onLeft: () => void;
}

type PendingAction = 'clear' | 'leave' | null;

export function ChatInfo({ chat, onLeft }: Props) {
  const closeDetails = useUiStore((state) => state.closeDetails);
  const updateMembership = useUpdateMembership();
  const clearHistory = useClearHistory();
  const leaveChat = useLeaveChat();
  const blockPeer = useBlockPeer();
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const online = usePresenceStore((state) =>
    chat.peer_user_id ? (state.onlineUserIds[chat.peer_user_id] ?? chat.peer_is_online) : false,
  );

  const isOwner = chat.membership.role === 'owner';
  const canLeave = chat.kind !== 'channel' || !isOwner;

  const toggle = (patch: Parameters<typeof updateMembership.mutate>[0]['patch']) =>
    updateMembership.mutate({ chatId: chat.id, patch });

  return (
    <aside className="details">
      <button
        className="icon-btn"
        type="button"
        aria-label="Скрыть информацию о чате"
        onClick={closeDetails}
        style={{ position: 'absolute', top: 14, right: 12, zIndex: 2 }}
      >
        <X size={18} />
      </button>

      <header className="topbar details-head">
        <strong>{chat.kind === 'channel' ? 'О канале' : 'О чате'}</strong>
      </header>

      <div className="details-profile">
        <Avatar name={chat.title} kind={chat.kind} size="large" />
        <h2>{chat.title}</h2>
        {chat.slug ? <p>@{chat.slug}</p> : null}
        {chat.kind === 'direct' ? (
          <span className={online ? 'online' : 'online offline'}>
            {online ? 'В сети' : chat.presence_label}
          </span>
        ) : (
          <span className="online offline">{chat.presence_label}</span>
        )}
      </div>

      {chat.description ? <div className="detail-copy">{chat.description}</div> : null}

      {chat.kind === 'group' && chat.members ? (
        <div className="detail-copy">
          <strong>Участники · {chat.member_count}</strong>
          <ul>
            {chat.members.map((member) => (
              <li key={member.user_id}>
                {member.display_name}
                {member.role !== 'member' ? ` · ${member.role}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {chat.kind === 'channel' ? (
        <div className="detail-copy">
          <strong>
            <Radio size={16} style={{ display: 'inline', marginRight: 6 }} />
            Канал для публикаций
          </strong>
          <span className="muted">
            Публиковать сообщения могут только владелец и администраторы.
          </span>
        </div>
      ) : null}

      <div className="chat-preferences">
        <button
          type="button"
          onClick={() => toggle({ is_pinned: !chat.membership.is_pinned })}
          disabled={updateMembership.isPending}
        >
          {chat.membership.is_pinned ? <PinOff size={17} /> : <Pin size={17} />}
          {chat.membership.is_pinned ? 'Открепить' : 'Закрепить'}
        </button>

        <button
          type="button"
          onClick={() =>
            toggle({
              muted_until: chat.membership.is_muted
                ? null
                : new Date(Date.now() + MUTE_HOURS * 3600_000).toISOString(),
            })
          }
          disabled={updateMembership.isPending}
        >
          {chat.membership.is_muted ? <Bell size={17} /> : <BellOff size={17} />}
          {chat.membership.is_muted
            ? 'Включить уведомления'
            : `Отключить на ${MUTE_HOURS} часов`}
        </button>

        <button
          type="button"
          onClick={() => toggle({ is_archived: !chat.membership.is_archived })}
          disabled={updateMembership.isPending}
        >
          {chat.membership.is_archived ? <ArchiveRestore size={17} /> : <Archive size={17} />}
          {chat.membership.is_archived ? 'Вернуть из архива' : 'Архивировать'}
        </button>

        <button type="button" onClick={() => setPendingAction('clear')}>
          <Eraser size={17} />
          Очистить историю
        </button>

        {chat.kind === 'direct' && chat.peer_user_id ? (
          <button
            type="button"
            className="danger"
            onClick={() =>
              blockPeer.mutate({ chatId: chat.id, blocked: !chat.peer_is_blocked })
            }
            disabled={blockPeer.isPending}
          >
            <Ban size={17} />
            {chat.peer_is_blocked ? 'Разблокировать пользователя' : 'Заблокировать пользователя'}
          </button>
        ) : null}

        {canLeave ? (
          <button type="button" className="danger" onClick={() => setPendingAction('leave')}>
            {chat.kind === 'channel' ? <LogOut size={17} /> : <Trash2 size={17} />}
            {chat.kind === 'channel' ? 'Покинуть канал' : 'Удалить чат'}
          </button>
        ) : null}
      </div>

      {pendingAction === 'clear' ? (
        <ConfirmDialog
          title="Очистить историю?"
          description="Сообщения исчезнут только у вас. У других участников они останутся."
          confirmLabel="Очистить"
          destructive
          onCancel={() => setPendingAction(null)}
          onConfirm={() => {
            clearHistory.mutate(chat.id);
            setPendingAction(null);
          }}
        />
      ) : null}

      {pendingAction === 'leave' ? (
        <ConfirmDialog
          title={chat.kind === 'channel' ? 'Покинуть канал?' : 'Удалить чат?'}
          description={
            chat.kind === 'channel'
              ? 'Канал исчезнет из вашего списка. Подписаться снова можно в любой момент.'
              : 'Чат исчезнет из вашего списка. У собеседника переписка сохранится.'
          }
          confirmLabel={chat.kind === 'channel' ? 'Покинуть' : 'Удалить'}
          destructive
          onCancel={() => setPendingAction(null)}
          onConfirm={() => {
            leaveChat.mutate(chat.id, { onSuccess: onLeft });
            setPendingAction(null);
          }}
        />
      ) : null}
    </aside>
  );
}
